// Phone helpers — client records store `countryCode` and `phone` inconsistently
// (code empty or "+212", number with or without the code, leading 0, stray spaces),
// so everything is normalised to digits before formatting.

const DEFAULT_COUNTRY_CODE = "212";
// Calling codes we recognise when the code is embedded in the number itself.
const KNOWN_CODES = ["212", "213", "216", "33", "34", "32", "41", "44", "49", "39", "1"];

const digitsOnly = (value) => String(value ?? "").replace(/\D/g, "");

/**
 * Split raw values into a clean {code, national} pair (digits only).
 * Returns null when there is no usable number.
 */
export const parsePhone = (countryCode, phone) => {
    let code = digitsOnly(countryCode);
    let national = digitsOnly(phone);

    // International prefix typed as 00 (e.g. 00212...)
    if (national.startsWith("00")) national = national.slice(2);

    if (!national) return null;

    if (code && national.startsWith(code) && national.length > code.length + 6) {
        // Code duplicated inside the number: "212" + "212612345678"
        national = national.slice(code.length);
    } else if (!code) {
        // No code stored: detect one embedded in the number, else assume the default
        const embedded = national.length > 10 && KNOWN_CODES.find(c => national.startsWith(c));
        if (embedded) {
            code = embedded;
            national = national.slice(embedded.length);
        } else {
            code = DEFAULT_COUNTRY_CODE;
        }
    }

    // Local trunk prefix: +212 0612... → +212 612...
    if (national.length > 9 && national.startsWith("0")) national = national.slice(1);

    return {code, national};
};

/** Human-readable number, e.g. "+212 6 12 34 56 78". Returns "—" when empty. */
export const formatPhone = (countryCode, phone) => {
    const parsed = parsePhone(countryCode, phone);
    if (!parsed) return "—";
    const {code, national} = parsed;

    // 9-digit Moroccan / French-style numbers: X XX XX XX XX
    if (national.length === 9) {
        return `+${code} ${national[0]} ${national.slice(1).match(/.{2}/g).join(" ")}`;
    }
    // 10 digits: groups of two
    if (national.length === 10) {
        return `+${code} ${national.match(/.{2}/g).join(" ")}`;
    }
    return `+${code} ${national}`;
};

/** `tel:` URI for click-to-call, or null when there is no number. */
export const phoneHref = (countryCode, phone) => {
    const parsed = parsePhone(countryCode, phone);
    return parsed ? `tel:+${parsed.code}${parsed.national}` : null;
};
